<template>
    <div class="kt-portlet kt-portlet--mobile" id="combinations-table" ref="element">
        <div class="kt-portlet__head kt-portlet__head--lg">
            <div class="kt-portlet__head-label">
                <h3 class="kt-portlet__head-title">{{ i18n("views.Combinations.ViewTitle") }}</h3>
            </div>

            <div class="kt-portlet__head-toolbar">
                <div class="kt-portlet__head-wrapper">
                    <a @click="addCombination" class="btn btn-clean btn-icon-sm">
                        <i class="la la-plus"></i> {{ i18n("views.Combinations.NewCombination") }}
                    </a>
                </div>
            </div>
        </div>

        <table class="combinations-table">
            <TableCombinationListHeader />
            <tbody>
                <tr class="combinations-table-no-data-row" v-if="combinations.length === 0">
                    <td colspan="4">{{ i18n("views.Combinations.NoCombinationsAdded") }}</td>
                </tr>
                <TableCombinationListRow
                    v-for="combination in combinations"
                    :key="combination.id"
                    :combination="combination"
                    :ref="(el) => setRowRef(el, combination.id)"
                    @on-remove-combination="removeCombination"
                />
            </tbody>
        </table>

        <div class="kt-portlet__foot">
            <div class="kt-form__actions">
                <div class="row">
                    <div class="col-lg-3 col-xl-3"></div>
                    <div class="col-lg-9 col-xl-6 d-flex justify-content-end">
                        <button @click="saveCombinations" type="submit" class="btn btn-success">
                            <i class="la la-save"></i>{{ i18n("views.PortalController.GeneralSave") }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { ITableCombination, ITableCombinationToCreateOrUpdate } from "@/data/model/ITableCombination";
    import { i18n } from "@/portal/Trans";
    import { v4 as uuidv4 } from "uuid";
    import { onMounted, ref } from "vue";

    import TableCombinationListHeader from "@/portal/views/TableCombinationListHeader.vue";
    import TableCombinationListRow from "@/portal/views/TableCombinationListRow.vue";
    import { Factory } from "@/data/dao/Factory";
    import { TableDao } from "@/data/dao/service/TableDao";
    import { UiNotification } from "@/portal/ui/Notification";
    import { logError } from "@/shared/logging/errors";
    import {DaoError} from "@/data/dao/service/BaseServiceDao";
    import {exceptionTypes} from "@/data/util/Config";
    import {translations} from "@/shared/localization/translations";

    const tableDao = Factory.getInstance<TableDao>(TableDao);
    
    const combinations = ref<ITableCombinationToCreateOrUpdate[]>([]);
    const rowRefs = ref<Record<string, typeof TableCombinationListRow>>({});

    async function init() {
      await loadCombinations();
    }
    
    onMounted(async () => {
      await init();
    });

    async function loadCombinations() {
      combinations.value = (await tableDao.getTableCombinations({})).map(c => {
        return {
          id: c.id,
          preferredMinimumOccupancy: c.preferredMinimumOccupancy,
          maximumOccupancy: c.maximumOccupancy,
          tables: c.tables.sort((item1, item2) => item1.order - item2.order).map(t => {
            return t.tableId
          }),
          sortOrder: c.sortOrder,
          isActive: c.isActive,
        }
      });
    }

    async function removeCombination(combinationToRemove: ITableCombination) {
      UiNotification.confirmWithOptions({
        message: i18n("general.reallyRemove"),
        confirmBtn: translations.general.ok,
        cancelBtn: translations.general.cancel,
        confirmed: async () => {
          try {
            await tableDao.deleteTableCombination({tableCombinationId: combinationToRemove.id});
            combinations.value = combinations.value.filter((c) => c.id !== combinationToRemove.id);
            UiNotification.showSuccess(i18n("general.successfullyRemoved"));

          } catch (ex) {
            logError(ex);
            if (ex instanceof DaoError) {
              if (ex.type === exceptionTypes.tableCombinationInUseException) {
                UiNotification.showException(i18n("views.Combinations.TableCombinationInUseError"));
                return;
              }
            }

            UiNotification.showException(i18n("general.failedRemove"));
          }
        },
      });
      
     
    }

    function setRowRef(row, id) {
        if (row) {
            rowRefs.value[id] = row;
        } else {
            delete rowRefs.value[id];
        }
    }

    async function saveCombinations() {
      if (isDataValid()) {
        try {
          const combinationsToSave = combinations.value!;
          await tableDao.updateTableCombinations({
            combinations: combinationsToSave,
          });
          UiNotification.showSuccess(i18n("general.successfullyUpdated"));
          await loadCombinations();
        } catch (ex) {
          logError(ex);
          if (ex instanceof DaoError) {
            if (ex.type === exceptionTypes.tableCombinationInUseException) {
              UiNotification.showException(i18n("views.Combinations.TableCombinationInUseError"));
              return;
            }
          }

          UiNotification.showException(i18n("general.failedUpdate"));
        }
      }
    }

    function isDataValid(): boolean {
        let hasErrors = false;

        Object.values(rowRefs.value).forEach((r) => {
            if (r.validate() === false) {
                hasErrors = true;
            }
        });

        return !hasErrors;
    }

    function addCombination(e) {
        e.preventDefault();
        e.stopPropagation();

        combinations.value.push({
            id: uuidv4(),
            tables: [],
            preferredMinimumOccupancy: undefined,
            maximumOccupancy: undefined,
            sortOrder: undefined,
            isActive: false,
        });
    }
</script>

<style scoped lang="scss">
    .combinations-table {
        margin: 7.5px;

        .combinations-table-no-data-row {
            td {
                padding: 2rem;
                color: #7f849a;
                text-align: center;
            }
        }
    }
</style>
